import trim from 'lodash/trim';
import isEmail from 'validator/lib/isEmail';
import isEmpty from 'validator/lib/isEmpty';
import isLength from 'validator/lib/isLength';
import isURL from 'validator/lib/isURL';

import { pluralize } from 'src/lib/format';

import { VALIDATION_RULE } from './rules';
import type { ValidationRuleConfig } from './types';

const chars = (count: number) => pluralize(count, 'character', 'characters');

const validatorRequired = (rule: ValidationRuleConfig, value: string) => {
  const ruleMessage = (rule && typeof rule === 'object' && rule.message) || 'Input needed';

  if (value != null && !isEmpty(trim(value))) {
    return null;
  }
  return ruleMessage;
};

const validatorMaxLength = (rule: ValidationRuleConfig, value: string) => {
  const ruleValue =
    (rule && typeof rule === 'object' && rule.value) || (typeof rule === 'number' ? rule : 0);
  const ruleMessage =
    (rule && typeof rule === 'object' && rule.message) ||
    `This value is too long. The maximum length is ${ruleValue} ${chars(ruleValue)}`;

  if (isLength(trim(value), { max: ruleValue })) {
    return null;
  }
  return ruleMessage;
};

const validatorOneComma = (rule: ValidationRuleConfig, value: string) => {
  const ruleMessage =
    (rule && typeof rule === 'object' && rule.message) ||
    'More than one comma is not allowed in the input';

  const regex = new RegExp(/(,).+(,)/);

  if (value.match(regex)) {
    return ruleMessage;
  }
  return null;
};

const validatorMinLength = (rule: ValidationRuleConfig, value: string) => {
  const ruleValue =
    (rule && typeof rule === 'object' && rule.value) || (typeof rule === 'number' ? rule : 0);
  const ruleMessage =
    (rule && typeof rule === 'object' && rule.message) ||
    `This value is too short. The minimum length is ${ruleValue} ${chars(ruleValue)}`;

  if (isLength(trim(value), { min: ruleValue })) {
    return null;
  }
  return ruleMessage;
};

const validatorIsUrl = (rule: ValidationRuleConfig, value: string) => {
  const ruleMessage =
    (rule && typeof rule === 'object' && rule.message) ||
    'Specified URL seems to have an incorrect value. Please, re-check the URL';

  if (isURL(value)) {
    return null;
  }
  return ruleMessage;
};

const validatorIsEmail = (rule: ValidationRuleConfig, value: string) => {
  const ruleMessage =
    (rule && typeof rule === 'object' && rule.message) || 'Please enter a valid email address';

  if (isEmail(value)) {
    return null;
  }
  return ruleMessage;
};

const MIN_PASSWORD_LENGTH = 8;
const MAX_PASSWORD_LENGTH = 128;
const validatorPassword = (_rule: ValidationRuleConfig, value: string) => {
  if (!value || isEmpty(trim(value))) {
    return {
      rule: VALIDATION_RULE.required,
      message: 'Please enter password',
    };
  }
  if (!isLength(trim(value), { min: MIN_PASSWORD_LENGTH })) {
    return {
      rule: VALIDATION_RULE.minLength,
      message: `Password should be at least ${MIN_PASSWORD_LENGTH} characters long`,
    };
  }
  if (!isLength(trim(value), { max: MAX_PASSWORD_LENGTH })) {
    return {
      rule: VALIDATION_RULE.maxLength,
      message: `Please, use a maximum password length of ${MAX_PASSWORD_LENGTH} or less characters`,
    };
  }
  return null;
};

export const VALIDATORS = {
  [VALIDATION_RULE.required]: validatorRequired,
  [VALIDATION_RULE.maxLength]: validatorMaxLength,
  [VALIDATION_RULE.minLength]: validatorMinLength,
  [VALIDATION_RULE.isUrl]: validatorIsUrl,
  [VALIDATION_RULE.isEmail]: validatorIsEmail,
  [VALIDATION_RULE.password]: validatorPassword,
  [VALIDATION_RULE.oneComma]: validatorOneComma,
};

const COMMON_EMAIL_DOMAINS = [
  'icloud.com',
  'gmail.com',
  'me.com',
  'mac.com',
  'hotmail.com',
  'verizon.com',
  'yahoo.com',
  'live.com',
  'outlook.com',
  'aol.com',
];

export const isCommonEmail = (email: string): boolean =>
  COMMON_EMAIL_DOMAINS.some((d) => email.endsWith(d));
